import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { toast } from "react-toastify";
import api from "../../utils/api";

const languages = ["en", "hy", "ru"];
const languageLabels = { en: "English", hy: "Armenian", ru: "Russian" };

const emptySeo = () => ({
  meta_title_hy: "",
  meta_title_en: "",
  meta_title_ru: "",
  meta_description_hy: "",
  meta_description_en: "",
  meta_description_ru: "",
  og_title_hy: "",
  og_title_en: "",
  og_title_ru: "",
  og_description_hy: "",
  og_description_en: "",
  og_description_ru: "",
  canonical_url: "",
});

const getError = (errorsObj, field) => {
  const v = errorsObj[field];
  if (Array.isArray(v) && v.length) return v[0];
  if (typeof v === "string") return v;
  return "";
};

export default function PageSeoSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [pages, setPages] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [seo, setSeo] = useState(emptySeo());

  const loadPages = () =>
    api.get("admin/content/page-seo").then((res) => {
      const list = res.data?.data?.pages || [];
      setPages(list);
      if (!selectedSlug && list.length) {
        setSelectedSlug(list[0].slug);
      }
      return list;
    });

  const loadPageSeo = (slug) => {
    if (!slug) return Promise.resolve();
    return api.get(`admin/content/page-seo/${slug}`).then((res) => {
      const row = res.data?.data?.seo || {};
      setSeo({
        ...emptySeo(),
        ...row,
        canonical_url: row.canonical_url || "",
      });
    });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadPages()
      .then((list) => {
        if (cancelled || !list.length) return;
        const slug = selectedSlug || list[0].slug;
        return loadPageSeo(slug);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedSlug) return;
    let cancelled = false;
    setLoading(true);
    loadPageSeo(selectedSlug)
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSlug]);

  const handleFieldChange = (field, value) => {
    setSeo((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async () => {
    if (!selectedSlug) return;
    setSaving(true);
    setErrors({});
    try {
      await api.put(`admin/content/page-seo/${selectedSlug}`, seo);
      await loadPageSeo(selectedSlug);
      toast.success("SEO settings saved.");
    } catch (e) {
      if (e?.errors && typeof e.errors === "object") setErrors(e.errors);
      const message = e?.errors
        ? "Please fix the validation errors."
        : (e?.response?.data?.message || "Failed to save SEO settings.");
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const selectedPage = pages.find((p) => p.slug === selectedSlug);

  if (loading && !pages.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography component="h2" variant="h5" sx={{ my: 3, borderBottom: "1px solid", borderColor: "divider", pb: 1 }}>
        Page SEO
      </Typography>

      <FormControl fullWidth size="small">
        <FormLabel sx={{ mb: 0.5 }}>Public page</FormLabel>
        <Select
          value={selectedSlug}
          onChange={(e) => setSelectedSlug(e.target.value)}
          displayEmpty
        >
          {pages.map((page) => (
            <MenuItem key={page.slug} value={page.slug}>
              {page.label} ({page.path})
            </MenuItem>
          ))}
        </Select>
        {selectedPage?.path && (
          <FormHelperText>
            Default URL path: {selectedPage.path}. Leave Canonical URL empty to use the locale-aware site URL.
          </FormHelperText>
        )}
      </FormControl>

      <FormControl fullWidth error={!!getError(errors, "canonical_url")}>
        <FormLabel sx={{ mb: 0.5 }} htmlFor="seo-canonical">Canonical URL (optional)</FormLabel>
        <OutlinedInput
          id="seo-canonical"
          fullWidth
          size="small"
          placeholder="https://languageon.co/hy/about"
          value={seo.canonical_url || ""}
          onChange={(e) => handleFieldChange("canonical_url", e.target.value)}
        />
        {getError(errors, "canonical_url") && (
          <FormHelperText error>{getError(errors, "canonical_url")}</FormHelperText>
        )}
      </FormControl>

      {languages.map((lang) => (
        <Box key={lang} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            {languageLabels[lang]}
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }} error={!!getError(errors, `meta_title_${lang}`)}>
            <FormLabel sx={{ mb: 0.5 }}>Meta Title</FormLabel>
            <OutlinedInput
              fullWidth
              size="small"
              value={seo[`meta_title_${lang}`] || ""}
              onChange={(e) => handleFieldChange(`meta_title_${lang}`, e.target.value)}
            />
            {getError(errors, `meta_title_${lang}`) && (
              <FormHelperText error>{getError(errors, `meta_title_${lang}`)}</FormHelperText>
            )}
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }} error={!!getError(errors, `meta_description_${lang}`)}>
            <FormLabel sx={{ mb: 0.5 }}>Meta Description</FormLabel>
            <OutlinedInput
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={seo[`meta_description_${lang}`] || ""}
              onChange={(e) => handleFieldChange(`meta_description_${lang}`, e.target.value)}
            />
            {getError(errors, `meta_description_${lang}`) && (
              <FormHelperText error>{getError(errors, `meta_description_${lang}`)}</FormHelperText>
            )}
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }} error={!!getError(errors, `og_title_${lang}`)}>
            <FormLabel sx={{ mb: 0.5 }}>Open Graph Title</FormLabel>
            <OutlinedInput
              fullWidth
              size="small"
              value={seo[`og_title_${lang}`] || ""}
              onChange={(e) => handleFieldChange(`og_title_${lang}`, e.target.value)}
            />
            {getError(errors, `og_title_${lang}`) && (
              <FormHelperText error>{getError(errors, `og_title_${lang}`)}</FormHelperText>
            )}
          </FormControl>

          <FormControl fullWidth error={!!getError(errors, `og_description_${lang}`)}>
            <FormLabel sx={{ mb: 0.5 }}>Open Graph Description</FormLabel>
            <OutlinedInput
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={seo[`og_description_${lang}`] || ""}
              onChange={(e) => handleFieldChange(`og_description_${lang}`, e.target.value)}
            />
            {getError(errors, `og_description_${lang}`) && (
              <FormHelperText error>{getError(errors, `og_description_${lang}`)}</FormHelperText>
            )}
          </FormControl>
        </Box>
      ))}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !selectedSlug}>
          {saving ? <CircularProgress size={24} /> : "Save"}
        </Button>
      </Box>
    </Box>
  );
}
